import { NumberField } from "@/components/form/number-field";
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useTenantQuota, useUpdateTenantQuota } from "@/api/settings";

export const Route = createFileRoute("/_app/settings/limits")({
  component: LimitsSettings,
});

const FIELDS: { key: string; label: string; help: string }[] = [
  {
    key: "maxConcurrentPipelines",
    label: "Max concurrent pipelines",
    help: "Executions allowed to run at the same time for this tenant.",
  },
  {
    key: "maxApiCallsPerHour",
    label: "Max API calls / hour",
    help: "Rate limit across all API keys and sessions.",
  },
  {
    key: "maxRecordsPerDay",
    label: "Max records / day",
    help: "Total records processed across all pipelines in a rolling 24h window.",
  },
];

function LimitsSettings() {
  const toast = useToast();
  const { data, isLoading, error, refetch } = useTenantQuota();
  const update = useUpdateTenantQuota();

  const [values, setValues] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setValues({
        maxConcurrentPipelines: String(data.maxConcurrentPipelines ?? ""),
        maxApiCallsPerHour: String(data.maxApiCallsPerHour ?? ""),
        maxRecordsPerDay: String(data.maxRecordsPerDay ?? ""),
      });
      setNotes(data.notes ?? "");
      setDirty(false);
    }
  }, [data]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;

  async function save() {
    const body: Record<string, unknown> = { notes: notes.trim() || null };
    for (const f of FIELDS) {
      const n = Number(values[f.key]);
      if (Number.isFinite(n)) body[f.key] = n;
    }
    await update.mutateAsync(body);
    setDirty(false);
    toast.success("Limits updated");
  }

  return (
    <PageContainer maxWidth={640}>
      <PageHeader
        title="Usage limits"
        description="Per-tenant quotas enforced by the platform."
        actions={
          <Button variant="contained" disabled={!dirty || update.isPending} onClick={save}>
            Save
          </Button>
        }
      />
      <Card>
        <CardContent>
          <Stack spacing={2.5}>
            {FIELDS.map((f) => (
              <NumberField
                key={f.key}
                size="small"
                integer
                label={f.label}
                helperText={f.help}
                value={values[f.key] ?? ""}
                onValueChange={(value) => {
                  setValues((p) => ({ ...p, [f.key]: value == null ? "" : String(value) }));
                  setDirty(true);
                }}
              />
            ))}
            <TextField
              size="small"
              label="Notes"
              multiline
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setDirty(true);
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Last updated {data?.updatedAt ? new Date(data.updatedAt).toLocaleString() : "—"}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
